import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusPill } from "@/components/common";
import { Users, UserPlus, Crown, Shield, Edit3, Eye, X, Mail, AlertTriangle } from "lucide-react";

const ROLE_INFO = {
  owner:  { icon: Crown,  color: "var(--amber-500)", label: "Owner",  hint: "Full control: company, billing, team, RFQs/quotes" },
  admin:  { icon: Shield, color: "var(--amber-400)", label: "Admin",  hint: "Everything except managing roles or removing members" },
  editor: { icon: Edit3,  color: "var(--ink)",       label: "Editor", hint: "Create RFQs / submit quotes / upload docs" },
  viewer: { icon: Eye,    color: "var(--ink-mute)",  label: "Viewer", hint: "Read-only access" },
};

export default function TeamPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ members: [], pending_invites: [], my_team_role: "owner" });
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [inviteForm, setInviteForm] = useState({ email: "", role: "editor", message: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/team"); setData(data); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const isOwner = data.my_team_role === "owner";
  const canInvite = isOwner || data.my_team_role === "admin";

  const submitInvite = async (e) => {
    e.preventDefault(); setBusy(true); setErr(""); setMsg("");
    try {
      await api.post("/team/invite", inviteForm);
      setMsg(`Invite sent to ${inviteForm.email}. They will receive an accept link via email (currently dev-log mode — see admin logs for token).`);
      setInviteForm({ email: "", role: "editor", message: "" });
      load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const changeRole = async (id, role) => {
    try { await api.put(`/team/${id}/role`, { role }); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const removeMember = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the team? They will lose access immediately.`)) return;
    try { await api.delete(`/team/${id}`); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this pending invite?")) return;
    try { await api.delete(`/team/invite/${id}`); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <>
      <PageHeader
        title="Team"
        subtitle={`Manage who has access to your company and what they can do. Your role: ${ROLE_INFO[data.my_team_role]?.label || data.my_team_role}`}
        actions={<span className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>{data.members.length} member{data.members.length === 1 ? "" : "s"} · {data.pending_invites.length} pending</span>}
      />

      {err && <div style={{ color: "var(--red)", marginBottom: 12 }}>{err}</div>}
      {msg && <div style={{ color: "var(--emerald)", marginBottom: 12 }}>{msg}</div>}

      {/* Invite form */}
      {canInvite && (
        <form onSubmit={submitInvite} className="card" style={{ padding: 20, marginBottom: 24 }} data-testid="invite-form">
          <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--amber-400)", marginBottom: 14 }}>
            <UserPlus size={12} style={{ display: "inline-block", marginRight: 6, verticalAlign: -2 }} />INVITE A TEAMMATE
          </div>
          <div className="rfq-grid-3">
            <div><label className="label">Email</label>
              <input className="input" data-testid="invite-email" type="email" required value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} placeholder="colleague@company.ca" />
            </div>
            <div><label className="label">Role</label>
              <select className="input" data-testid="invite-role" value={inviteForm.role} onChange={e => setInviteForm(f => ({ ...f, role: e.target.value }))}>
                {Object.entries(ROLE_INFO).filter(([k]) => isOwner || k !== "owner").map(([k, v]) => (
                  <option key={k} value={k}>{v.label} — {v.hint}</option>
                ))}
              </select>
            </div>
            <div style={{ alignSelf: "end" }}>
              <button data-testid="send-invite" disabled={busy} className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }}>
                {busy ? "Sending…" : "Send invite"}
              </button>
            </div>
          </div>
          <div><label className="label" style={{ marginTop: 12 }}>Message (optional)</label>
            <input className="input" value={inviteForm.message} onChange={e => setInviteForm(f => ({ ...f, message: e.target.value }))} placeholder="Hi! Joining you on CanadaMRO Exchange…" />
          </div>
        </form>
      )}

      {!canInvite && (
        <div className="awaiting-approval" style={{ margin: "0 0 24px" }}>
          <AlertTriangle size={16} color="var(--amber-400)" style={{ flexShrink: 0 }} />
          <div>Your team role is <b>{ROLE_INFO[data.my_team_role]?.label}</b>. Only owners and admins can invite teammates.</div>
        </div>
      )}

      {/* Members list */}
      <div className="card" data-testid="members-list">
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--amber-400)" }}>
            <Users size={12} style={{ display: "inline-block", marginRight: 6, verticalAlign: -2 }} />MEMBERS
          </span>
          <span className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>{data.members.length}</span>
        </div>
        <table className="data">
          <thead><tr><th>Member</th><th>Role</th><th>Verification</th><th>Joined</th><th></th></tr></thead>
          <tbody>
            {data.members.map(m => {
              const tr = m.team_role || "owner";
              const ri = ROLE_INFO[tr] || ROLE_INFO.owner;
              const Icon = ri.icon;
              const isMe = m.id === user?.id;
              return (
                <tr key={m.id} data-testid={`member-row-${m.id}`}>
                  <td data-label="Member">
                    <div style={{ fontWeight: 600 }}>{m.full_name} {isMe && <span style={{ color: "var(--amber-400)", fontSize: 11, marginLeft: 6 }}>(you)</span>}</div>
                    <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>{m.email}</div>
                  </td>
                  <td data-label="Role" style={{ color: ri.color, fontWeight: 600 }}>
                    {isOwner && !isMe ? (
                      <select className="input" data-testid={`role-select-${m.id}`} value={tr} onChange={e => changeRole(m.id, e.target.value)} style={{ padding: "4px 8px", fontSize: 13 }}>
                        {Object.entries(ROLE_INFO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    ) : (
                      <span><Icon size={12} style={{ display: "inline-block", verticalAlign: -1, marginRight: 4 }} /> {ri.label}</span>
                    )}
                  </td>
                  <td data-label="Verification"><StatusPill status={m.verification_status || "pending"} /></td>
                  <td data-label="Joined" className="font-mono" style={{ fontSize: 12, color: "var(--ink-mute)" }}>
                    {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td data-label="">
                    {isOwner && !isMe && (
                      <button data-testid={`remove-${m.id}`} onClick={() => removeMember(m.id, m.full_name)} className="btn btn-danger" style={{ padding: "4px 10px", fontSize: 11 }}>
                        <X size={12} /> Remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pending invites */}
      {data.pending_invites.length > 0 && (
        <div className="card" style={{ marginTop: 20 }} data-testid="invites-list">
          <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--line)" }}>
            <span className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--amber-400)" }}>
              <Mail size={12} style={{ display: "inline-block", marginRight: 6, verticalAlign: -2 }} />PENDING INVITES
            </span>
          </div>
          <table className="data">
            <thead><tr><th>Email</th><th>Role</th><th>Sent</th><th>Expires</th><th></th></tr></thead>
            <tbody>
              {data.pending_invites.map(inv => {
                const ri = ROLE_INFO[inv.role] || ROLE_INFO.editor;
                return (
                  <tr key={inv.id} data-testid={`invite-row-${inv.id}`}>
                    <td data-label="Email">{inv.email}</td>
                    <td data-label="Role" style={{ color: ri.color }}>{ri.label}</td>
                    <td data-label="Sent" className="font-mono" style={{ fontSize: 12, color: "var(--ink-mute)" }}>{new Date(inv.created_at).toLocaleDateString()}</td>
                    <td data-label="Expires" className="font-mono" style={{ fontSize: 12, color: "var(--ink-mute)" }}>{new Date(inv.expires_at).toLocaleDateString()}</td>
                    <td data-label="">
                      {canInvite && (
                        <button data-testid={`revoke-${inv.id}`} onClick={() => revokeInvite(inv.id)} className="btn btn-danger" style={{ padding: "4px 10px", fontSize: 11 }}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
